import React from 'react'
import { RoomHeader } from '../../../../generated/api/chat-room'
import { isImageType } from '../../../../helpers/types'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { AvatarInner } from '../../../common/Avatar'
import { Button, ButtonVariant } from '../../../common/Button'
import { ToastType } from '../../../common/Toast/toast-type'

const AVATAR_PLACEHOLDER = {
  user: 'avatar',
  interests: 'interests',
  society: 'society',
} as const

const AVATAR_INPUT_ID = 'room-avatar-image-input'

interface RoomAvatarFieldProps {
  header: RoomHeader
  canEdit: boolean
  isSaving: boolean
  onPickImage: (file: File) => void
  onRemoveImage: () => void
}

export const RoomAvatarField = ({
  header,
  canEdit,
  isSaving,
  onPickImage,
  onRemoveImage,
}: RoomAvatarFieldProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const avatar = (
    <AvatarInner
      alt={`${header.title} avatar`}
      url={header.avatar.url}
      initials={header.avatar.initials}
      placeholder={AVATAR_PLACEHOLDER[header.avatar.kind]}
      size={60}
    />
  )

  if (!canEdit) return avatar

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!isImageType(file.type)) {
      setToastNotification(
        ToastType.DANGER,
        translate('uploadImage.invalidImageType')
      )
      return
    }
    onPickImage(file)
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <label
        htmlFor={AVATAR_INPUT_ID}
        title={translate('chatSettings.actionSheet.changeChatRoomImage')}
        className={`rounded-full p-1 ${
          isSaving ? 'opacity-50' : 'cursor-pointer hover:bg-neutral-90'
        }`}
      >
        {avatar}
        <input
          id={AVATAR_INPUT_ID}
          type="file"
          accept="image/png, image/jpg, image/jpeg"
          className="hidden"
          onChange={onChange}
          disabled={isSaving}
          data-testid="room-settings-avatar-input"
        />
      </label>
      {header.avatar.url && (
        <Button
          type="button"
          size="xs"
          variant={ButtonVariant.TEXT}
          label={translate('chatSettings.actionSheet.deleteChatRoomImage')}
          onClick={onRemoveImage}
          disabled={isSaving}
          data-testid="room-settings-avatar-remove"
        />
      )}
    </div>
  )
}
